import { FC, useState } from 'react';
import { DebouncedInput } from '@/components/functional/DebouncedInput/DebouncedInput.tsx';
import SelectInput from '@/components/functional/SelectInput/SelectInput.tsx';
import TextInput from '@/components/functional/TextInput/TextInput.tsx';
import DatePickerInput from '@/components/functional/DatePickerInput/DatePickerInput.tsx';
import { Button } from 'react-daisyui';
import CheckboxInput from '@/components/functional/CheckboxInput/CheckboxInput.tsx';
import OtpInput from '@/components/functional/OtpInput/OtpInput.tsx';
import TextArea from '@/components/functional/TextArea/TextArea.tsx';
import { Oklch } from 'culori';
import ColorPicker from '@/components/functional/ColorPicker/ColorPicker.tsx';
import FileInput from '@/components/functional/FileInput/FileInput.tsx';
import RangeInput from '@/components/functional/RangeInput/RangeInput.tsx';
import Radio from '@/components/functional/RadioInput/Radio.tsx';
import CheckboxGroup from '@/components/functional/CheckboxInput/CheckboxGroup.tsx';
import ImageInput from '@/components/functional/ImageInput/ImageInput.tsx';

const InputsPage: FC = () => {
  const [value, setValue] = useState('');
  const [file, setFile] = useState<File>();
  const [debouncedValue, setDebouncedValue] = useState('');
  const [selectValue, setSelectValue] = useState('');
  const [dateValue, setDateValue] = useState('');
  const [otpValue, setOtpValue] = useState('');
  const [rangeValue, setRangeValue] = useState<[number, number]>([20, 60]);
  const [rangeSingleValue, setRangeSingleValue] = useState<number>(20);
  const [currentColor, setCurrentColor] = useState<Oklch | undefined>();
  const [radioValue, setRadioValue] = useState<string | null>(null);
  const [checkboxValues, setCheckboxValues] = useState<string[]>([]);
  const [image, setImage] = useState<File | string | null>(null);

  const [error, setError] = useState('');
  const [showLabel, setShowLabel] = useState(true);

  return (
    <div className="flex flex-col gap-4 w-[400px]">
      <Button color="primary" onClick={() => setError((prev) => (prev ? '' : 'Error'))}>
        Toggle error
      </Button>
      <Button color="primary" onClick={() => setShowLabel((prev) => !prev)}>
        Toggle labels
      </Button>
      <TextInput
        label={showLabel ? 'Text input' : undefined}
        error={error}
        placeholder="placeholder"
        value={value}
        onChange={(e) => setValue(`${e.target.value}`)}
      />
      <DebouncedInput
        label={showLabel ? 'Debounced text input' : undefined}
        error={error}
        placeholder="placeholder"
        value={debouncedValue}
        onChange={(val) => setDebouncedValue(`${val}`)}
      />
      <TextArea
        label={showLabel ? 'Textarea input' : undefined}
        error={error}
        placeholder="placeholder"
        value={value}
        onChange={(e) => setValue(`${e.target.value}`)}
      />
      <SelectInput
        label={showLabel ? 'Select' : undefined}
        error={error}
        placeholder="placeholder"
        value={selectValue}
        onChange={(val) => setSelectValue(val)}
        options={[
          {
            value: 'Single',
            label: 'Single',
          },
          {
            value: 'Complicated',
            label: 'Complicated',
          },
          {
            value: 'In Relationship',
            label: 'In Relationship',
          },
        ]}
      />
      <SelectInput
        label={showLabel ? 'Select searchable' : undefined}
        error={error}
        placeholder="placeholder"
        value={selectValue}
        searchable
        onChange={(val) => setSelectValue(val)}
        options={[
          {
            value: 'Single',
            label: 'Single',
          },
          {
            value: 'Complicated',
            label: 'Complicated',
          },
          {
            value: 'In Relationship',
            label: 'In Relationship',
          },
        ]}
      />
      <FileInput
        label={showLabel ? 'File input' : undefined}
        error={error}
        placeholder="placeholder"
        value={file}
        accept="image/*"
        onChange={(files) => setFile(files[0])}
      />
      <CheckboxInput color="primary" label={showLabel ? 'Checkbox input' : undefined} error={error} />
      {JSON.stringify(checkboxValues, null, 2)}
      <CheckboxGroup.Group
        value={checkboxValues}
        onChange={(val) => setCheckboxValues(val)}
        error={error}
        color="primary"
      >
        <div className="flex flex-col">
          <CheckboxGroup.Input value="check1" label="checkbox group elem1" />
          <CheckboxGroup.Input value="check2" label="checkbox group elem2" />
        </div>
      </CheckboxGroup.Group>
      <DatePickerInput
        label={showLabel ? 'Date picker' : undefined}
        error={error}
        value={dateValue}
        onChange={(val) => setDateValue(val ?? '')}
        placeholder="placeholder"
      />
      <RangeInput
        label={showLabel ? 'Range input' : undefined}
        error={error}
        value={rangeValue}
        color="secondary"
        onChange={(val) => setRangeValue(val)}
        min={0}
        max={200}
      />
      <RangeInput
        label={showLabel ? 'Range input with values' : undefined}
        error={error}
        value={rangeValue}
        showValues
        onChange={(val) => setRangeValue(val)}
        renderValue={(val) => `$${val}`}
        min={0}
        max={200}
      />
      <RangeInput
        label={showLabel ? 'Range single input' : undefined}
        error={error}
        single
        value={rangeSingleValue}
        onChange={(val) => setRangeSingleValue(val)}
      />
      <Radio.Group color="primary" value={radioValue} onChange={(val) => setRadioValue(val)} error={error}>
        <div className="flex flex-col">
          <Radio.Input value="Radio value 1" label="Radio value 1" />
          <Radio.Input value="Radio value 2" label="Radio value 2" />
        </div>
      </Radio.Group>
      <ImageInput
        className="w-16 h-16"
        label={showLabel ? 'Image input' : undefined}
        error={error}
        value={image}
        onChange={(val) => setImage(val)}
      />
      <OtpInput
        label={showLabel ? 'Otp Input' : undefined}
        error={error}
        numInputs={5}
        value={otpValue}
        onChange={(val) => setOtpValue(val)}
      />
      <ColorPicker className="max-w-[400px]" onChange={setCurrentColor} currentColor={currentColor} />
    </div>
  );
};

export default InputsPage;
